// client/src/components/cookie/CookieConsent.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  CATEGORY_DESCRIPTIONS,
  CATEGORY_LABELS,
  CONSENT_CATEGORIES,
  acceptAllConsent,
  needsConsentDecision,
  readConsent,
  rejectNonEssentialConsent,
  writeConsent,
} from "../../utils/cookieConsent";

import "./CookieConsent.css";

// ─── Cookie Settings modal ─────────────────────────────────────────────────

function CategoryToggle({ id, category, label, description, checked, disabled, onChange }) {
  const trackId = `cookie-toggle-${category}`;

  return (
    <div className="cookie-category">
      <div className="cookie-category__row">
        <div className="cookie-category__info">
          <span className="cookie-category__name">{label}</span>
          <span className="cookie-category__desc">{description}</span>
        </div>

        {disabled ? (
          <span className="cookie-always-on" aria-label={`${label}: always on`}>
            Always on
          </span>
        ) : (
          <label className="cookie-toggle" htmlFor={trackId} aria-label={`${label}: ${checked ? "on" : "off"}`}>
            <input
              id={trackId}
              type="checkbox"
              checked={checked}
              onChange={(e) => onChange(category, e.target.checked)}
            />
            <span className="cookie-toggle__track" />
          </label>
        )}
      </div>
    </div>
  );
}

function CookieSettingsModal({ open, onClose, onSave, onAcceptAll, onRejectAll }) {
  const [categories, setCategories] = useState(() => {
    const stored = readConsent();
    if (stored) return { ...stored.categories };
    return {
      [CONSENT_CATEGORIES.essential]: true,
      [CONSENT_CATEGORIES.analytics]: false,
      [CONSENT_CATEGORIES.marketing]: false,
      [CONSENT_CATEGORIES.preferences]: false,
    };
  });

  // Sync state when modal opens so it reflects current consent
  useEffect(() => {
    if (open) {
      const stored = readConsent();
      if (stored) {
        setCategories({ ...stored.categories });
      } else {
        setCategories({
          [CONSENT_CATEGORIES.essential]: true,
          [CONSENT_CATEGORIES.analytics]: false,
          [CONSENT_CATEGORIES.marketing]: false,
          [CONSENT_CATEGORIES.preferences]: false,
        });
      }
    }
  }, [open]);

  const firstFocusRef = useRef(null);

  useEffect(() => {
    if (open && firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, [open]);

  // Trap focus inside modal
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  const handleToggle = useCallback((category, value) => {
    if (category === CONSENT_CATEGORIES.essential) return;
    setCategories((prev) => ({ ...prev, [category]: value }));
  }, []);

  const handleSave = useCallback(() => {
    onSave(categories);
  }, [categories, onSave]);

  if (!open) return null;

  return (
    <div
      className="cookie-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Cookie settings"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={handleKeyDown}
    >
      <div className="cookie-modal">
        <div className="cookie-modal__header">
          <h2>Cookie Settings</h2>
          <button
            ref={firstFocusRef}
            type="button"
            className="cookie-modal__close"
            onClick={onClose}
            aria-label="Close cookie settings"
          >
            ✕
          </button>
        </div>

        <div className="cookie-modal__body">
          <p>
            We use cookies to provide essential site functionality and, with your
            consent, to improve your experience. You can manage your preferences
            below. Your choice is remembered for future visits and can be changed
            at any time via Cookie Settings in the footer.
          </p>

          {Object.values(CONSENT_CATEGORIES).map((category) => (
            <CategoryToggle
              key={category}
              id={`toggle-${category}`}
              category={category}
              label={CATEGORY_LABELS[category]}
              description={CATEGORY_DESCRIPTIONS[category]}
              checked={Boolean(categories[category])}
              disabled={category === CONSENT_CATEGORIES.essential}
              onChange={handleToggle}
            />
          ))}
        </div>

        <div className="cookie-modal__footer">
          <button
            type="button"
            className="cookie-btn cookie-btn--primary"
            onClick={handleSave}
          >
            Save my preferences
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn--secondary"
            onClick={onAcceptAll}
          >
            Accept all
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn--ghost"
            onClick={onRejectAll}
          >
            Reject non-essential
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Banner ────────────────────────────────────────────────────────────────

function CookieBanner({ onAcceptAll, onRejectAll, onCustomise }) {
  return (
    <div
      className="cookie-banner"
      role="region"
      aria-label="Cookie consent"
    >
      <div className="cookie-banner__inner">
        <div className="cookie-banner__text">
          <p>
            We use essential cookies to make this site work. With your consent
            we may also use analytics, marketing, and preference cookies. See
            our{" "}
            <Link to="/cookie-policy">Cookie Policy</Link> for details.
          </p>
        </div>

        <div className="cookie-banner__actions">
          <button
            type="button"
            className="cookie-btn cookie-btn--primary"
            onClick={onAcceptAll}
            id="cookie-accept-all"
          >
            Accept all
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn--secondary"
            onClick={onRejectAll}
            id="cookie-reject-non-essential"
          >
            Reject non-essential
          </button>
          <button
            type="button"
            className="cookie-btn cookie-btn--ghost"
            onClick={onCustomise}
            id="cookie-customise"
          >
            Customise
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main export ───────────────────────────────────────────────────────────

/**
 * Mounts a cookie consent banner on first visit and a settings modal.
 *
 * Usage:
 *   <CookieConsent />              — renders banner + modal
 *   <CookieConsent openSettings /> — programmatically opens modal
 *
 * Communicating with the Footer:
 *   The Footer calls `window.dispatchEvent(new Event("openCookieSettings"))`
 *   to open the modal from the footer link without lifting state.
 */
export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Decide whether to show the banner once on mount
  useEffect(() => {
    setShowBanner(needsConsentDecision());
  }, []);

  // Listen for the global "openCookieSettings" event from the Footer
  useEffect(() => {
    const handler = () => setShowModal(true);
    window.addEventListener("openCookieSettings", handler);
    return () => window.removeEventListener("openCookieSettings", handler);
  }, []);

  const dismissBannerAndModal = useCallback(() => {
    setShowBanner(false);
    setShowModal(false);
  }, []);

  const handleAcceptAll = useCallback(() => {
    acceptAllConsent();
    dismissBannerAndModal();
  }, [dismissBannerAndModal]);

  const handleRejectAll = useCallback(() => {
    rejectNonEssentialConsent();
    dismissBannerAndModal();
  }, [dismissBannerAndModal]);

  const handleCustomise = useCallback(() => {
    setShowBanner(false);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(
    (categories) => {
      writeConsent(categories, true);
      dismissBannerAndModal();
    },
    [dismissBannerAndModal],
  );

  const handleModalClose = useCallback(() => {
    // If the user closes without deciding, only dismiss the modal.
    // The banner will stay dismissed if we had already hidden it (e.g. they
    // came from the footer link). If they came from the banner customise flow,
    // we treat it as reject-non-essential to avoid leaving the decision open.
    if (needsConsentDecision()) {
      rejectNonEssentialConsent();
    }
    setShowModal(false);
  }, []);

  return (
    <>
      {showBanner && !showModal && (
        <CookieBanner
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onCustomise={handleCustomise}
        />
      )}

      <CookieSettingsModal
        open={showModal}
        onClose={handleModalClose}
        onSave={handleSave}
        onAcceptAll={handleAcceptAll}
        onRejectAll={handleRejectAll}
      />
    </>
  );
}
